import AdvanceTable from "components/common/advance-table/AdvanceTable";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Dropdown, Row, Spinner } from "react-bootstrap";
import useApiKeys from "hooks/apikeys/useApiKeys";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CardDropdown from "components/common/CardDropdown";
import useFormattedDate from "hooks/useFormatDate";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { Empty, Input, Select } from "antd";
import { ApiKeyDetailsDrawer } from "./ApiKeyDetailsDrawer";
import ApiKeyFormDrawer from "./ApiKeyFormDrawer";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import IconButton from "components/common/IconButton";

export const ApiKeys = () => {
  const token = sessionStorage.getItem("authToken");

  const {
    apiKeys,
    loading,
    fetchApiKeyById,
    selectedKey,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    error,
  } = useApiKeys(token);

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const isSearchRequest = searchTerm.trim().length >= 3;

  const handleViewApiKey = async (id) => {
    try {
      await fetchApiKeyById(id);
      setShowDetailsDrawer(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const handleEditApiKey = async (id) => {
    try {
      await fetchApiKeyById(id);
      setFormMode("edit");
      setShowFormDrawer(true);
    } catch (err) {
      toast.error("Failed to load API key details", { theme: "colored" });
    }
  };

  const confirmDelete = (id) => {
    setKeyToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteApiKey(keyToDelete);
    setShowDeleteModal(false);
  };

  const filteredApiKeys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const hasSearch = term.length >= 3;

    return (apiKeys || []).filter((item) => {
      const status = item?.active === true ? "ACTIVE" : "DISABLED";
      const byStatus = selectedStatus === "ALL" || selectedStatus === status;

      if (!hasSearch) return byStatus;

      const haystack = [item?.id, item?.name, item?.clientId, item?.key, status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return byStatus && haystack.includes(term);
    });
  }, [apiKeys, searchTerm, selectedStatus]);

  const totalElements = filteredApiKeys.length;
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  const { formatDateTime } = useFormattedDate();
  const formatKey = (key) => `${key.slice(0, 6)}...${key.slice(-4)}`;
  const formatText = (text) => (text && text.length > 15 ? `${text.slice(0, 15)}...` : text || "-");

  const getStatusStyle = (isActive) => {
    const style = isActive
      ? { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" }
      : { bg: "#fbe9e7", text: "#c62828", border: "#f5c6cb" };
    return {
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "0.7rem",
      fontWeight: 600,
      border: `1px solid ${style.border}`,
      backgroundColor: style.bg,
      color: style.text,
      display: "inline-block",
    };
  };
const columns = [
  {
    accessorKey: "name",
    header: "Name",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => (
      <div 
        title={getValue()} 
        className="text-900 fw-normal" // Normal Value
        style={{ 
          maxWidth: 150, 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap",
          fontSize: 13 
        }}
      >
        {getValue()}
      </div>
    ),
  },
  {
    accessorKey: "clientId",
    header: "Client ID",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const val = getValue() || "";
      return (
        <div 
          className="text-900 font-monospace fw-normal" // Normal Value
          title={val} 
          style={{ 
            maxWidth: 100, 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            fontSize: 13,
            cursor: 'help'
          }}
        >
          {val}
        </div>
      );
    },
  },
  {
    accessorKey: "key",
    header: "Key",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const val = getValue() || "";
      return (
        <div 
          className="text-900 font-monospace fw-normal" // Normal Value
          title={val} 
          style={{ 
            maxWidth: 100, 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            fontSize: 13,
            cursor: 'help'
          }}
        >
          {val}
        </div>
      );
    },
  },
  {
    accessorKey: "active",
    header: "Status",
    meta: { 
      headerProps: { className: "text-center text-900 fw-bold" }, 
      cellProps: { className: "text-center" } 
    },
    cell: ({ getValue }) => {
      const isActive = getValue() === true;
      const value = isActive ? "ACTIVE" : "DISABLED";
      
      const pillStyle = {
        ...getStatusStyle(isActive), 
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "0.7rem",
        lineHeight: 1.2,
        fontWeight: 600, // Status text stays semi-bold for visibility
        display: "inline-block",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        textTransform: "uppercase"
      };

      return (
        <div className="text-center">
          <span style={pillStyle}>{value}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "expiresAt",
    header: "Expires At",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => (
      <span className="text-500 fw-normal" style={{ fontSize: 12 }}>
        {formatDateTime(getValue())}
      </span>
    ),
  },
  {
    accessorKey: "description",
    header: "Description",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const desc = getValue() || "";
      return (
        <div 
          className="text-900 fw-normal" 
          title={desc}
          style={{ 
            maxWidth: 150, 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            fontSize: 12 
          }}
        >
          {desc}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    meta: { 
      headerProps: { className: "text-center text-900 fw-bold" }, 
      cellProps: { className: "text-center" } 
    },
    cell: ({ row }) => (
      <CardDropdown drop="start">
        <div className="py-2 fw-normal">
          <Dropdown.Item onClick={() => handleViewApiKey(row.original.id)}>View</Dropdown.Item>
          <Guard privilege={PRIV.UPDATE_APIKEYS}>
            <Dropdown.Item onClick={() => handleEditApiKey(row.original.id)}>Edit</Dropdown.Item>
          </Guard>
          <Dropdown.Divider />
          <Guard privilege={PRIV.DELETE_APIKEYS}>
            <Dropdown.Item onClick={() => confirmDelete(row.original.id)} className="text-danger">Delete</Dropdown.Item>
          </Guard>
        </div>
      </CardDropdown>
    ),
  },
];

  const paginatedApiKeys = useMemo(() => {
    const start = page * pageSize;
    return filteredApiKeys.slice(start, start + pageSize);
  }, [filteredApiKeys, page, pageSize]);

  const table = useAdvanceTable({
    data: paginatedApiKeys,
    columns,
    sortable: true,
    selection: true,
    pagination: false,
  });

  const handleSubmitApiKey = async (data) => {
    try {
      if (formMode === "edit" && selectedKey?.id) {
        await updateApiKey(selectedKey.id, data);
      } else {
        await createApiKey(data);
      }
      setShowFormDrawer(false);
    } catch (err) {
      toast.error(err.message || "Operation failed", { theme: "colored" });
    }
  };

  return (
    <>
      <WidgetSectionTitle
        icon="layer-group"
        title="API Keys"
        subtitle="Manage and monitor server API keys."
        className="mb-3 mt-3"
      />

      {/* ================= ACTION BUTTON ================= */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            onClick={() => { setFormMode("create"); setShowFormDrawer(true); }}
            style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
          >
            Add API Key
          </IconButton>
        </Col>
      </Row>

      {/* ================= SEARCH & FILTER ROW ================= */}

<Row className="mb-2 g-3 align-items-center">
  <Col md={8} lg={9}>
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Input
        prefix={<span style={{ fontSize: 14 }}>🔍</span>}
        placeholder='Search by "name", "id", or "clientId" (min. 3 chars)...'
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        allowClear
        /* Removed size="large" as it was overriding the 30px height */
        style={{ 
          borderRadius: 12, 
          height: 30, 
          flex: "1 1 300px", 
          maxWidth: 300,
          fontSize: "13px",
          display: "flex",
          alignItems: "center",
          borderColor: searchTerm.length > 0 && searchTerm.length < 3 ? "#ffa940" : ""
        }}
        suffix={
          loading && isSearchRequest ? (
            <Spinner animation="border" size="sm" variant="success" style={{ width: 12, height: 12 }} />
          ) : searchTerm.length > 0 && searchTerm.length < 3 && (
            <small className="text-warning" style={{ fontSize: "10px" }}>
              Type {3 - searchTerm.length} more...
            </small>
          )
        }
      />
      <Select
        /* Removed size="large" to allow the 28px height to take control */
        value={selectedStatus}
        onChange={(value) => {
          setSelectedStatus(value || "ALL");
          setPage(0);
        }}
        options={[
          { label: "🌍 ALL STATUS", value: "ALL" },
          { label: "✅ ACTIVE", value: "ACTIVE" },
          { label: "🚫 DISABLED", value: "DISABLED" },
        ]}
        style={{ 
          minWidth: 180, 
          height: 28,
          fontSize: "13px"
        }}
        /* This is crucial for AntD Select to respect small heights */
        listHeight={250}
      />
    </div>
  </Col>
  <Col md={4} lg={3} className="text-end">
    <div style={{ fontSize: 13, color: "#64748b", lineHeight: "30px" }}>
      Results: <span className="fw-semibold text-dark">{totalElements}</span> of <span className="fw-semibold text-dark">{apiKeys.length}</span>
    </div>
  </Col>
</Row>

      {/* ================= TABLE AREA ================= */}
      <AdvanceTableProvider {...table}>
        {loading && totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Empty description="No API keys found" />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      {/* Modals & Drawers */}
      <ApiKeyDetailsDrawer
        selectedKey={selectedKey}
        loading={loading}
        showDrawer={showDetailsDrawer}
        setShowDrawer={setShowDetailsDrawer}
        updateApiKey={updateApiKey}
      />
      <ApiKeyFormDrawer
        open={showFormDrawer}
        onClose={() => { setShowFormDrawer(false); setFormMode("create"); }}
        onSubmit={handleSubmitApiKey}
        mode={formMode}
        initialValues={formMode === "edit" ? selectedKey : null}
        loading={loading}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="api key"
        loading={loading}
      />
    </>
  );
};